import {
    Box,
    Button,
    Checkbox,
    Collapse,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    InputGroup,
    InputRightElement,
    Select,
    SimpleGrid,
    Text,
    useToast,
    VStack,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Roles, User } from "../../interfaes/user";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Joi from "joi";
import { errorFormat, FormattedError, getErrorMessage, hasErrorMessage } from "../../utils/formUtils";
import { DEFAULTUSER } from "../../interfaes/fixture";
import { onAddNewUser, onUpdatePassword, onUpdateUser } from "../../http/users";
import ImageUpload from "../../components/ImageUpload";
import { isTeacherOnly } from "../../utils/common";

type ManageUsersPropsType = {
    shouldReturn: boolean;
    role: string;
    returnUserID: (id: string) => void;
};

export default function ManageUsers({ shouldReturn, returnUserID, role }: ManageUsersPropsType) {
    const { state } = useLocation();

    const [loading, setLoading] = useState<boolean>(false);

    const { id } = useParams();
    const schema = Joi.object({
        displayName: Joi.string()
            .required(),

        address: Joi.string().required(),
        image: Joi.string().allow('').optional(),
        gender: Joi.string().required(),
        phone: Joi.string().regex(/^\d{10}$/) // Ensure it contains exactly 10 digits
            .message('Phone number must be exactly 10 digits').required(),
        age: Joi.number().optional(),
        description: Joi.string().allow('').optional(),
        roles: id === 'add' || shouldReturn ? Joi.array().min(1).optional() : Joi.optional().allow(null),
        credencials: id === 'add' || shouldReturn ? Joi.object({
            username: Joi.number()
                .required(),
            password: Joi.string().required()
        }) : Joi.optional().allow(null),
    });
    const toast = useToast();

    const [userObj, setUserObj] = useState<User>(
        state ? {
            "displayName": state.displayName,
            "image": state.image ? state.image : undefined,
            "description": "-",
            "gender": state.gender,
            "address": state.address,
            "phone": state.phone,
            "roles": id === 'add' ? state.roles : state.roles,
            "age": 22,
            "credencials": id === 'add' || shouldReturn ? {
                "username": state.userName,
                "password": ""
            } : undefined,
        } : DEFAULTUSER
    );
    const [formErrors, setFormErrors] = useState<FormattedError[]>([]);
    const { t } = useTranslation('users');
    const [show, setShow] = React.useState(false)
    const handleClick = () => setShow(!show);
    const navigate = useNavigate();

    // Password Update State
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [passwordData, setPasswordData] = useState({ oldPassword: '', newPassword: '' });
    const [passLoading, setPassLoading] = useState(false);


    const validateObj = () => {
        const { error } = schema.validate(userObj);
        if (error) {
            setFormErrors(errorFormat(error.details));
            return false;
        }

        setFormErrors([]);
        return true;
    }
    const [initialPhone, setInitialPhone] = useState<string>("");
    const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
    const [verifyPasswordInput, setVerifyPasswordInput] = useState("");

    // Set initial phone when data loads
    useEffect(() => {
        if (state && state.phone) {
            setInitialPhone(state.phone);
        }
    }, [state]);

    const handleUpdateUser = async () => {
        if (validateObj()) {
            if (id && id !== 'add' && !shouldReturn) {
                // Check if phone number changed
                if (userObj.phone !== initialPhone) {
                    setIsVerifyModalOpen(true);
                    return;
                }
            }
            // If no sensitive change, proceed as normal
            await executeUpdate();
        }
    }

    const handleVerifyAndSave = async () => {
        if (!verifyPasswordInput) {
            toast({ title: 'Error', description: "Password is required", status: 'error', duration: 3000 });
            return;
        }
        await executeUpdate(verifyPasswordInput);
        setIsVerifyModalOpen(false);
        setVerifyPasswordInput("");
    }

    const executeUpdate = async (password?: string) => {
        try {
            if (id) {
                setLoading(true);
                if (id === 'add' || shouldReturn) {
                    const response = await onAddNewUser(userObj);
                    if (shouldReturn) {
                        returnUserID(response.data.id)
                    } else {
                        navigate(-1)
                    }
                    toast({
                        title: 'Added',
                        description: "User Added Successfully!",
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                    })
                } else {
                    await onUpdateUser(userObj, id, password);
                    navigate(-1)
                }
            }

        } catch (e: any) {
            toast({
                title: 'Error',
                description: e?.response?.data?.message || "Failed to update user",
                status: 'error',
                duration: 3000,
                isClosable: true,
            })
        } finally {
            setLoading(false);
        }
    }

    const handlePasswordUpdate = async () => {
        if (!passwordData.oldPassword || !passwordData.newPassword) {
            toast({
                title: 'Error',
                description: "Please fill all password fields",
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }
        setPassLoading(true);
        try {
            await onUpdatePassword(passwordData.oldPassword, passwordData.newPassword);
            toast({
                title: 'Success',
                description: "Password updated successfully",
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            setShowChangePassword(false);
            setPasswordData({ oldPassword: '', newPassword: '' });
        } catch (e: any) {
            toast({
                title: 'Error',
                description: e?.response?.data?.message || "Failed to update password",
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setPassLoading(false);
        }
    }

    useEffect(() => {
        if (shouldReturn) {
            let r: Roles = role as Roles;
            setUserObj({ ...userObj, roles: [r] })
        }
    }, [shouldReturn]);
    const callback = (url: string) => {
        let newObj = { ...userObj };
        newObj.image = url;
        setUserObj({ ...newObj });
    };

    const isAddMode = id === 'add' || shouldReturn;

    return (
        <Box>
            <Text fontSize={"24px"} fontWeight={900}>
                {t('title')}
            </Text>
            <Box p={"24px"} background={"#ffffff"} mt={"18px"} borderRadius={'12px'}>
                <VStack spacing={6} align="stretch">

                    {/* Image Upload - Centered */}
                    <Flex justify="center" mb={4}>
                        <Box w={"150px"}>
                            <ImageUpload onSetImage={callback} imageUrl={userObj.image} />
                        </Box>
                    </Flex>

                    {/* Main Form Fields */}
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                        <FormControl isInvalid={hasErrorMessage(formErrors, 'displayName')}>
                            <FormLabel>{t('displayName')}</FormLabel>
                            <Input
                                onChange={(e) => setUserObj({ ...userObj, displayName: e.target.value || '' })}
                                value={userObj.displayName}
                                variant={"filled"} required />
                            <FormErrorMessage>{getErrorMessage(formErrors, 'displayName')}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={hasErrorMessage(formErrors, 'address')}>
                            <FormLabel>{t('address')}</FormLabel>
                            <Input
                                onChange={(e) => setUserObj({ ...userObj, address: e.target.value || '' })}
                                value={userObj.address}
                                variant={"filled"} required />
                            <FormErrorMessage>{getErrorMessage(formErrors, 'address')}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={hasErrorMessage(formErrors, 'gender')}>
                            <FormLabel>{t('gender')}</FormLabel>
                            <Select
                                onChange={(e) => setUserObj({ ...userObj, gender: e.target.value || '' })}
                                value={userObj.gender}
                                background={'var(--chakra-colors-gray-100)'}>
                                <option value='M'>{t('male')}</option>
                                <option value='F'>{t('female')}</option>
                                <option value='U'>{t('others')}</option>
                            </Select>
                            <FormErrorMessage>{getErrorMessage(formErrors, 'gender')}</FormErrorMessage>
                        </FormControl>

                        <FormControl isInvalid={hasErrorMessage(formErrors, 'phone')}>
                            <FormLabel>{t('phone')}</FormLabel>
                            <Input
                                onChange={(e) => {
                                    if (userObj.credencials) {
                                        setUserObj({
                                            ...userObj, phone: e.target.value || '', credencials: {
                                                ...userObj.credencials, username: e.target.value || ''
                                            }
                                        })
                                    } else {
                                        // Even if no credentials object (edit mode), update phone
                                        setUserObj({ ...userObj, phone: e.target.value || '' })
                                    }
                                }}
                                value={userObj.phone}
                                variant={"filled"}
                                required
                                type={"number"}
                            />
                            <FormErrorMessage>{getErrorMessage(formErrors, 'phone')}</FormErrorMessage>
                        </FormControl>
                    </SimpleGrid>

                    {/* Roles - Read Only if Edit */}
                    <Box>
                        <Text fontSize={"16px"} fontWeight={500} mb={"6px"}>
                            {isAddMode ? t('selectRole') : "Assigned Roles"}
                        </Text>
                        <Box background={'var(--chakra-colors-gray-100)'} borderRadius={'6px'} padding={'16px'}
                            border={hasErrorMessage(formErrors, 'role') ? '1px solid red' : 'none'}>
                            {isAddMode ? (
                                <Flex wrap="wrap" gap={4}>
                                    {isTeacherOnly() ?
                                        ["THERAPIST", "PARENT"].map(key =>
                                            <Checkbox key={key} colorScheme='red' value={key}
                                                isChecked={userObj.roles ? userObj.roles.includes(key as Roles) : false}
                                                onChange={() => {
                                                    if (userObj.roles) {
                                                        let selectedRole = key as Roles;
                                                        let updatedRoles = userObj.roles;
                                                        if (userObj?.roles.includes(selectedRole)) {
                                                            updatedRoles = updatedRoles.filter(item => item !== selectedRole);
                                                        } else {
                                                            updatedRoles.push(selectedRole);
                                                        }
                                                        setUserObj({ ...userObj, roles: updatedRoles })
                                                    }
                                                }}>
                                                {t(`${key}`)}
                                            </Checkbox>
                                        ) :
                                        Object.keys(Roles).map(key =>
                                            <Checkbox key={key} colorScheme='red' value={key}
                                                isChecked={userObj.roles ? userObj.roles.includes(key as Roles) : false}
                                                onChange={() => {
                                                    if (userObj.roles) {
                                                        let selectedRole = key as Roles;
                                                        let updatedRoles = userObj.roles;
                                                        if (userObj?.roles.includes(selectedRole)) {
                                                            updatedRoles = updatedRoles.filter(item => item !== selectedRole);
                                                        } else {
                                                            updatedRoles.push(selectedRole);
                                                        }
                                                        setUserObj({ ...userObj, roles: updatedRoles })
                                                    }
                                                }}>
                                                {t(`${key}`)}
                                            </Checkbox>
                                        )
                                    }
                                </Flex>
                            ) : (
                                <Input
                                    value={userObj.roles?.map(r => t(`${r}`)).join(', ') || ''}
                                    isReadOnly
                                    border="none"
                                    _focus={{ border: "none", boxShadow: "none" }}
                                    padding={0}
                                    height="auto"
                                    fontWeight="500"
                                />
                            )}
                        </Box>
                        <Text color={"red"}>{getErrorMessage(formErrors, 'role')}</Text>
                    </Box>

                    {/* Password Field for New Users Only */}
                    {isAddMode && (
                        <FormControl isInvalid={hasErrorMessage(formErrors, 'credencials.password')}>
                            <FormLabel>{t('password')}</FormLabel>
                            <InputGroup size='md'>
                                <Input
                                    onChange={(e) => {
                                        if (userObj.credencials) {
                                            setUserObj({
                                                ...userObj,
                                                credencials: {
                                                    ...userObj.credencials,
                                                    password: e.target.value || ''
                                                }
                                            })
                                        }
                                    }}
                                    value={userObj?.credencials ? userObj.credencials.password : ''}
                                    variant={"filled"} required
                                    type={show ? 'text' : 'password'} />
                                <InputRightElement width='4.5rem'>
                                    <Button h='1.75rem' size='sm' onClick={handleClick}>
                                        {show ? t('login:hide') : t('login:show')}
                                    </Button>
                                </InputRightElement>
                            </InputGroup>
                        </FormControl>
                    )}

                    {/* Change Password Section for Existing Users */}
                    {!isAddMode && (
                        <Box borderTop="1px solid" borderColor="gray.200" pt={4}>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setShowChangePassword(!showChangePassword)}
                                mb={4}
                            >
                                {showChangePassword ? 'Cancel Change Password' : 'Change Password'}
                            </Button>

                            <Collapse in={showChangePassword} animateOpacity>
                                <VStack spacing={4} align="start" maxW="400px" p={4} bg="gray.50" borderRadius="md">
                                    <FormControl>
                                        <FormLabel>Old Password</FormLabel>
                                        <Input
                                            type="password"
                                            bg="white"
                                            value={passwordData.oldPassword}
                                            onChange={(e) => setPasswordData({ ...passwordData, oldPassword: e.target.value })}
                                        />
                                    </FormControl>
                                    <FormControl>
                                        <FormLabel>New Password</FormLabel>
                                        <Input
                                            type="password"
                                            bg="white"
                                            value={passwordData.newPassword}
                                            onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                                        />
                                    </FormControl>
                                    <Button colorScheme="green" onClick={handlePasswordUpdate} isLoading={passLoading}>
                                        Update Password
                                    </Button>
                                </VStack>
                            </Collapse>
                        </Box>
                    )}

                    {/* Bottom Actions */}
                    <Flex justifyContent={'flex-end'}>
                        <Button colorScheme='gray' size='lg' mr={'12px'} onClick={() => {
                            if (shouldReturn) {
                                returnUserID('');
                            } else {
                                navigate(-1)
                            }
                        }} isDisabled={loading}>
                            {t('cancel')}
                        </Button>

                        <Button colorScheme='blue' size='lg' onClick={handleUpdateUser} isLoading={loading}
                            isDisabled={loading}>
                            {t('save')}
                        </Button>
                    </Flex>
                </VStack>
            </Box>

            {/* Password Verification Modal */}
            <Modal isOpen={isVerifyModalOpen} onClose={() => setIsVerifyModalOpen(false)}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Verify Password</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text mb={4}>Changing your phone number requires password verification.</Text>
                        <FormControl>
                            <FormLabel>Current Password</FormLabel>
                            <Input
                                type="password"
                                value={verifyPasswordInput}
                                onChange={(e) => setVerifyPasswordInput(e.target.value)}
                            />
                        </FormControl>
                    </ModalBody>

                    <ModalFooter>
                        <Button colorScheme="gray" mr={3} onClick={() => setIsVerifyModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button colorScheme="blue" onClick={handleVerifyAndSave} isLoading={loading}>
                            Verify & Save
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    )
}
